import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { authStorage } from '../services/authStorage';
import { authAPI } from '../services/api';
import { syncWebSocketAuth } from '../hooks/useUnifiedWebSocket';
import { nativeBilling } from '../services/nativeBilling';
import { clearStoredPostAuthRedirect, consumePostAuthRedirect } from '../utils/authRedirect';
import { getPostAuthDefaultRoute, hasCompletedOnboarding } from '../utils/onboarding';

const AuthContext = createContext();

// Pull a human-readable message out of an API error. The FastAPI backend
// raises HTTPException -> { detail: "..." } (or a Pydantic array on 422),
// while some handlers use { message } / { errors: [{ msg }] }.
const extractApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === 'string' && data.detail) return data.detail;
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    const msgs = data.detail
      .map((d) => (typeof d === 'string' ? d : d?.msg))
      .filter(Boolean);
    if (msgs.length) return msgs.join(', ');
  }
  if (typeof data.message === 'string' && data.message) return data.message;
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const msgs = data.errors.map((e) => e?.msg || e?.message).filter(Boolean);
    if (msgs.length) return msgs.join(', ');
  }
  return fallback;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const applyAuthResponse = async (authResponse) => {
    const { user: nextUser, token, refresh_token } = authResponse;

    await authStorage.setToken(token);
    if (refresh_token) {
      await authStorage.setRefreshToken(refresh_token);
    }
    setUser(nextUser);
    setIsAuthenticated(true);

    // The websocket singleton authenticated with the PREVIOUS token at
    // handshake time; reconnect it so subscribes/sends act as the new
    // identity (guest mint or login in a long-lived tab).
    try { await syncWebSocketAuth(); } catch (_) {}

    return { success: true, user: nextUser };
  };

  // Force-logout handler – called by the 401 interceptor in api.js when
  // both the access token AND refresh token are invalid / expired.
  const handleForceLogout = useCallback(async () => {
    await authStorage.removeToken();
    await authStorage.removeRefreshToken();
    setUser(null);
    setIsAuthenticated(false);
    // Drop the socket still authenticated as the removed identity.
    try { await syncWebSocketAuth(); } catch (_) {}
  }, []);

  useEffect(() => {
    window.addEventListener('auth:force-logout', handleForceLogout);
    return () => window.removeEventListener('auth:force-logout', handleForceLogout);
  }, [handleForceLogout]);

  // On native app resume, proactively verify the token so that a stale
  // session is detected immediately instead of after the user navigates.
  useEffect(() => {
    const handleCheckToken = async () => {
      if (!isAuthenticated) return;
      try {
        // This fires through the axios interceptor – if the access token has
        // expired the interceptor will transparently refresh it.  If the
        // refresh also fails the interceptor dispatches auth:force-logout.
        await authAPI.getProfile();
      } catch (_) {
        // Handled by the interceptor / force-logout event.
      }
    };

    window.addEventListener('auth:check-token', handleCheckToken);
    return () => window.removeEventListener('auth:check-token', handleCheckToken);
  }, [isAuthenticated]);

  // Check for existing authentication on app startup and handle OAuth callbacks
  useEffect(() => {
    const checkAuth = async () => {
      // Check for OAuth callback token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const oauthToken = urlParams.get('token');
      const oauthRefreshToken = urlParams.get('refresh_token');
      const oauthSuccess = urlParams.get('oauth');
      
      if (oauthToken && oauthSuccess === 'success') {
        // OAuth callback - store tokens and authenticate
        try {
          await authStorage.setToken(oauthToken);
          if (oauthRefreshToken) {
            await authStorage.setRefreshToken(oauthRefreshToken);
          }
          const response = await authAPI.getProfile();
          setUser(response.data);
          setIsAuthenticated(true);

          // Scan-to-Play deep links carry their token in the URL; stay on the
          // play route (minus the consumed token params) instead of bouncing
          // to the app's default post-auth destination.
          const redirectTarget = window.location.pathname.startsWith('/play/')
            ? window.location.pathname
            : consumePostAuthRedirect(
                getPostAuthDefaultRoute(response.data),
                (target) =>
                  !(hasCompletedOnboarding(response.data) && target.startsWith('/onboarding'))
              );
          window.location.replace(redirectTarget);
          return;
        } catch (error) {
          console.error('OAuth callback failed:', error);
          await authStorage.removeToken();
          await authStorage.removeRefreshToken();
          setIsAuthenticated(false);
          setUser(null);
        }
        setIsLoading(false);
        return;
      }
      
      // Regular auth check – the 401 interceptor in api.js will automatically
      // attempt a refresh if the access token has expired, so a single
      // getProfile() call is sufficient.
      const token = await authStorage.getToken();
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          await authStorage.removeToken();
          await authStorage.removeRefreshToken();
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!nativeBilling.isSupported() || isLoading) {
      return undefined;
    }

    let isCancelled = false;

    const syncBillingIdentity = async () => {
      try {
        if (isAuthenticated && user?.id) {
          await nativeBilling.syncUser(user.id);
          return;
        }

        await nativeBilling.clearUser();
      } catch (error) {
        if (!isCancelled) {
          console.error('RevenueCat auth sync failed:', error);
        }
      }
    };

    syncBillingIdentity();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, isLoading, user?.id]);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      
      if (response.data.success) {
        return await applyAuthResponse(response.data);
      } else {
        return { 
          success: false, 
          message: response.data.message || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      return {
        success: false,
        message: extractApiErrorMessage(error, 'Login failed. Please try again.'),
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      
      if (response.data.success) {
        return await applyAuthResponse(response.data);
      } else {
        return { 
          success: false, 
          message: response.data.message || 'Registration failed' 
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      return {
        success: false,
        message: extractApiErrorMessage(error, 'Registration failed. Please try again.'),
      };
    }
  };

  const signInWithGoogle = async ({ inviteCode } = {}) => {
    try {
      if (Capacitor?.isNativePlatform?.() === true) {
        const { nativeAuth } = await import('../services/nativeAuth');
        const response = await nativeAuth.signInWithGoogle({ inviteCode });

        if (response?.success) {
          return await applyAuthResponse(response);
        }

        return {
          success: false,
          message: response?.message || 'Google authentication failed.',
        };
      }

      authAPI.googleOAuthWeb();
      return { success: true, redirecting: true };
    } catch (error) {
      console.error('Google sign-in error:', error);

      return {
        success: false,
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Google authentication failed.',
      };
    }
  };

  // Scan-to-Play: mint an ephemeral guest identity so a QR scan lands in a
  // live session with no account. The guest token is stored like any other
  // session, so reopening the link on the same phone resumes the same run.
  // Accepts a starterId string, or { joinCode } for "join my run" companions.
  const startGuestSession = async (starterIdOrParams) => {
    try {
      const response = await authAPI.createGuestSession(starterIdOrParams);
      if (response.data.success) {
        return await applyAuthResponse(response.data);
      }
      return { success: false, message: response.data.message || 'Could not start guest session' };
    } catch (error) {
      console.error('Guest session error:', error);
      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.detail || 'Could not start guest session',
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredPostAuthRedirect();
      await authStorage.removeToken();
      await authStorage.removeRefreshToken();
      setUser(null);
      setIsAuthenticated(false);
      // Drop the socket still authenticated as the logged-out identity.
      try { await syncWebSocketAuth(); } catch (_) {}
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile refresh error:', error);
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    signInWithGoogle,
    startGuestSession,
    logout,
    updateProfile,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 